import React, { useState } from "react";
import { Link } from "gatsby";
import { MDXProvider } from "@mdx-js/react";
import Layout from "./layout";
import settings from "../../data/settings.yaml";

const AutoLink = React.lazy(() => import("../utils/auto-link"));
const PageHeader = React.lazy(() => import("./page-header"));
const PageContent = React.lazy(() => import("./page-content"));
const Section = React.lazy(() => import("../ui/section"));
const SubSection = React.lazy(() => import("../ui/subsection"));

function ButtonLink({ to, children, ...props }) {
  return (
    <AutoLink
      to={to}
      className="btn btn-primary rounded-5 my-3 me-2"
      {...props}
    >
      {children}
    </AutoLink>
  );
}

function References({
  title = "References",
  className = "references text-primary text-cols-lg-2",
  ...props
} = {}) {
  return <div className={className} {...props} />;
}

function Page({
  frontmatter,
  components,
  children,
  empty = false,
  path,
  ...props
}) {
  const [showSidebar, setShowSidebar] = useState(false);
  const handleCloseSidebar = () => setShowSidebar(false);
  const handleShowSidebar = () => setShowSidebar(true);

  const defaultComponents = {
    ButtonLink,
    Section,
    SubSection,
    References,
    Link,
    a: AutoLink,
  };

  // Local components that are rendered if empty === false
  // and passed as context to the MDXProvider otherwise
  const LocalPageHeader = (props) => {
    const mergedProps = {
      peopleLabel: "By",
      ...frontmatter,
      ...props,
    };
    return <PageHeader {...mergedProps} />;
  };
  const LocalPageContent = (opts) => (
    <PageContent
      pageTitle={frontmatter.title}
      showSidebar={showSidebar}
      onClose={handleCloseSidebar}
      pagePath={path}
      {...opts}
    />
  );
  if (empty) {
    defaultComponents.PageHeader = LocalPageHeader;
    defaultComponents.PageContent = LocalPageContent;
  }

  return (
    <Layout
      title={frontmatter.title}
      showSidebar={showSidebar}
      handleShowSidebar={handleShowSidebar}
      {...props}
    >
      <React.Suspense fallback="Loading...">
        <MDXProvider components={{ ...defaultComponents, ...components }}>
          {empty ? (
            children
          ) : (
            <>
              <LocalPageHeader />
              {children && <LocalPageContent>{children}</LocalPageContent>}
            </>
          )}
        </MDXProvider>
      </React.Suspense>
    </Layout>
  );
}

export function PageHead({ title, lead, people, meta, ogImage, pathname }) {
  const authors = people
    ?.map((person) => (person.user ? person.user.full_name : person.name))
    .join(", ", " and ");

  return (
    <>
      <title>{title}</title>
      <meta property="og:title" content={title} />
      {!authors ? null : <meta name="author" id="author" content={authors} />}
      {!lead ? null : (
        <>
          <meta name="description" id="description" content={lead} />
          <meta property="og:description" content={lead} />
        </>
      )}
      {!meta
        ? null
        : meta.map((meta) => (
            <meta
              name={meta.name}
              id={meta.name}
              key={meta.name}
              content={meta.content}
            />
          ))}
      <meta
        property="og:image"
        content={
          ogImage
            ? `${settings.site.url}${ogImage.image.childImageSharp.fixed.src}`
            : `${settings.site.url}/default-og-image.jpg`
        }
      />
      <meta property="og:url" content={`${settings.site.url}${pathname}`} />
    </>
  );
}

export function PageHeadMdx({ data, location }) {
  return PageHead({ ...getFrontmatter(data), pathname: location.pathname });
}

export function getFrontmatter(data) {
  const frontmatter = data?.mdx?.frontmatter || {};
  frontmatter.dateModified = data.mdx?.parent?.modifiedTime;
  return frontmatter;
}

export default Page;
